import React, { createContext, useContext, useMemo } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@careercard/convex/convex/_generated/api';
import { useAuthState } from '../hooks/useAuthState';

export type UserProgress = {
  xp: {
    total_xp: number;
    current_level: number;
    xp_for_current_level: number;
    xp_for_next_level: number;
    progress_percentage: number;
  };
  badges: Array<{
    id: string;
    name: string;
    icon: string;
  }>;
};

interface XPContextValue {
  progress: UserProgress | null;
  loading: boolean;
  error: Error | null;
  refreshXP: () => Promise<void>;
}

const XPContext = createContext<XPContextValue | undefined>(undefined);

export function XPProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading: authLoading } = useAuthState();

  // Use Convex for real-time XP progress (replaces REST API call)
  const xpData = useQuery(
    api.gamification.getXp,
    isAuthenticated ? {} : "skip"
  );

  // Use Convex for real-time achievements/badges (replaces REST API call)
  const achievementsData = useQuery(
    api.gamification.listAchievements,
    isAuthenticated ? {} : "skip"
  );

  // Transform Convex data to match the expected UserProgress format
  const progress: UserProgress | null = useMemo(() => {
    if (!xpData) return null;

    // Calculate progress percentage
    const progressRange = xpData.xpForNextLevel - xpData.xpForCurrentLevel;
    const progressMade = xpData.totalXp - xpData.xpForCurrentLevel;
    const progressPercentage = progressRange > 0
      ? Math.min(100, Math.round((progressMade / progressRange) * 100))
      : 100;

    // Transform achievements to badges format
    const badges = (achievementsData ?? [])
      .filter((a: { earned: boolean }) => a.earned)
      .map((a: { id: string; name: string; category: string }) => ({
        id: a.id,
        name: a.name,
        icon: getCategoryIcon(a.category),
      }));

    return {
      xp: {
        total_xp: xpData.totalXp,
        current_level: xpData.currentLevel,
        xp_for_current_level: xpData.xpForCurrentLevel,
        xp_for_next_level: xpData.xpForNextLevel,
        progress_percentage: progressPercentage,
      },
      badges,
    };
  }, [xpData, achievementsData]);

  // Loading state: authenticated but waiting for Convex data
  const loading = isAuthenticated && !authLoading && xpData === undefined;

  // With Convex subscriptions, there's no manual refresh needed - data updates in real-time
  // We keep the interface for backward compatibility but it's essentially a no-op
  const refreshXP = async () => {
    // No-op: Convex subscriptions handle real-time updates automatically
  };

  const value: XPContextValue = {
    progress,
    loading,
    error: null, // Convex handles errors internally
    refreshXP,
  };

  return <XPContext.Provider value={value}>{children}</XPContext.Provider>;
}

/**
 * Get icon for achievement category
 */
function getCategoryIcon(category: string): string {
  const icons: Record<string, string> = {
    cards: '🃏',
    notes: '📝',
    reviews: '📊',
    streaks: '🔥',
    milestones: '🎯',
    tiers: '⭐',
  };
  return icons[category] || '🏆';
}

// eslint-disable-next-line react-refresh/only-export-components
export function useXP(): XPContextValue {
  const context = useContext(XPContext);
  if (context === undefined) {
    throw new Error('useXP must be used within an XPProvider');
  }
  return context;
}
